/**
 * PaymentDebugPanel — forensic-trail на странице платежа в админке.
 *
 * 4 таба:
 *   1. Чеки 54-ФЗ — FiscalReceipt список + retry-кнопка
 *   2. События   — PaymentEvent timeline (хронологический feed)
 *   3. Webhook'и — последние WebhookLog с раскрытием raw body
 *   4. Export    — скачать всё JSON-файлом
 */
import { useState } from 'react';
import { useCustom, useCustomMutation } from '@refinedev/core';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Activity,
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Download,
  FileText,
  Network,
  RefreshCw,
  Webhook,
} from 'lucide-react';
import { notify } from '@/lib/notify';

interface Props {
  paymentId: number;
  orderId: number | null;
}

interface ListResponse<T> {
  data: { data: { items: T[] } };
}

interface FiscalReceiptItem {
  id: number;
  provider: string;
  operation: string;
  status: string;
  idempotencyKey: string;
  externalCommandId: string | null;
  fiscalSign: string | null;
  fiscalDocNumber: number | null;
  fiscalDocDate: string | null;
  fnNumber: string | null;
  kktRegNumber: string | null;
  amount: string;
  currency: string;
  rawRequest: unknown;
  rawResponse: unknown;
  attempts: number;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
}

interface PaymentEventItem {
  id: number;
  paymentId: number | null;
  orderId: number | null;
  source: string;
  type: string;
  level: 'INFO' | 'WARN' | 'ERROR';
  message: string;
  payload: unknown;
  actorAdminId: number | null;
  durationMs: number | null;
  createdAt: string;
}

interface WebhookLogItem {
  id: number;
  provider: string;
  endpoint: string;
  method: string;
  ip: string | null;
  signatureHeader: string | null;
  rawBody: string | null;
  hmacVerified: boolean | null;
  parsedOk: boolean | null;
  errorText: string | null;
  responseStatus: number | null;
  responseBody: string | null;
  durationMs: number | null;
  receivedAt: string;
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('ru-RU');
  } catch {
    return iso;
  }
}

function CollapsibleJson({ data, label }: { data: unknown; label: string }) {
  const [open, setOpen] = useState(false);
  if (data === null || data === undefined) {return null;}
  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        {open ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
        {label}
      </button>
      {open && (
        <pre className="mt-1 overflow-auto rounded border bg-muted p-2 text-[10px] leading-tight">
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
}

function LevelBadge({ level }: { level: PaymentEventItem['level'] }) {
  const map = {
    INFO: { className: 'bg-blue-50 text-blue-700', label: 'INFO' },
    WARN: { className: 'bg-amber-50 text-amber-800', label: 'WARN' },
    ERROR: { className: 'bg-red-50 text-red-700', label: 'ERROR' },
  } as const;
  const m = map[level];
  return <Badge variant="outline" className={m.className}>{m.label}</Badge>;
}

function FiscalStatusBadge({ status }: { status: string }) {
  const map: Record<string, { className: string; Icon: typeof CheckCircle2 }> = {
    PENDING: { className: 'bg-gray-100 text-gray-700', Icon: Clock },
    SENT: { className: 'bg-blue-50 text-blue-700', Icon: Clock },
    SUCCESS: { className: 'bg-green-50 text-green-700', Icon: CheckCircle2 },
    FAILED: { className: 'bg-red-50 text-red-700', Icon: AlertCircle },
  };
  const m = map[status] ?? { className: 'bg-gray-100', Icon: Clock };
  const Icon = m.Icon;
  return (
    <Badge variant="outline" className={m.className}>
      <Icon className="size-3 mr-1" />
      {status}
    </Badge>
  );
}

export function PaymentDebugPanel({ paymentId, orderId }: Props) {
  const { result: receiptsResult, query: receiptsQuery } = useCustom<ListResponse<FiscalReceiptItem>>({
    url: orderId ? `/admin/fiscal/receipts/by-order/${orderId}` : '/admin/fiscal/receipts/by-order/0',
    method: 'get',
    queryOptions: { enabled: Boolean(orderId) },
  });
  const refetchReceipts = receiptsQuery.refetch;

  const { result: eventsResult, query: eventsQuery } = useCustom<ListResponse<PaymentEventItem>>({
    url: `/admin/payments/${paymentId}/events`,
    method: 'get',
  });
  const refetchEvents = eventsQuery.refetch;

  const { result: webhooksResult } = useCustom<ListResponse<WebhookLogItem>>({
    url: `/admin/payments/${paymentId}/webhooks`,
    method: 'get',
  });

  const { mutate: retryReceipt } = useCustomMutation();

  const receipts: FiscalReceiptItem[] = receiptsResult?.data?.data?.data?.items ?? [];
  const events: PaymentEventItem[] = eventsResult?.data?.data?.data?.items ?? [];
  const webhooks: WebhookLogItem[] = webhooksResult?.data?.data?.data?.items ?? [];

  function handleRetry(receiptId: number) {
    retryReceipt(
      {
        url: `/admin/fiscal/receipts/${receiptId}/retry`,
        method: 'post',
        values: {},
      },
      {
        onSuccess: () => {
          notify.success('Чек заново отправлен в очередь');
          refetchReceipts();
          refetchEvents();
        },
        onError: (error) => {
          notify.error(
            `Ошибка retry: ${error instanceof Error ? error.message : String(error)}`,
          );
        },
      },
    );
  }

  function handleExport() {
    if (!paymentId) {return;}
    const url = `/api/admin/payments/${paymentId}/export`;
    fetch(url, { credentials: 'include' })
      .then((r) => r.json())
      .then((data) => {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `payment-${paymentId}-debug.json`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch((err) => notify.error(`Export failed: ${String(err)}`));
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-2 text-base">
          <span className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Debug & Forensic Trail
          </span>
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="size-3 mr-1" />
            Export JSON
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="fiscal">
          <TabsList>
            <TabsTrigger value="fiscal">
              <FileText className="size-3 mr-1" /> Чеки 54-ФЗ ({receipts.length})
            </TabsTrigger>
            <TabsTrigger value="events">
              <Network className="size-3 mr-1" /> События ({events.length})
            </TabsTrigger>
            <TabsTrigger value="webhooks">
              <Webhook className="size-3 mr-1" /> Webhooks ({webhooks.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="fiscal" className="space-y-3">
            {receipts.length === 0 ? (
              <div className="text-sm text-muted-foreground py-4">Чеков 54-ФЗ нет</div>
            ) : (
              receipts.map((r) => (
                <div key={r.id} className="rounded border p-3 text-xs space-y-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{r.provider}</Badge>
                      <Badge variant="outline">{r.operation}</Badge>
                      <FiscalStatusBadge status={r.status} />
                    </div>
                    {r.status !== 'SUCCESS' && (
                      <Button size="sm" variant="outline" onClick={() => handleRetry(r.id)}>
                        <RefreshCw className="size-3 mr-1" />
                        Retry
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-muted-foreground">
                    <span>c_num</span>
                    <span className="font-mono">{r.idempotencyKey}</span>
                    <span>commandId</span>
                    <span className="font-mono">{r.externalCommandId ?? '—'}</span>
                    <span>ФД</span>
                    <span className="font-mono">{r.fiscalDocNumber ?? '—'}</span>
                    <span>ФП</span>
                    <span className="font-mono">{r.fiscalSign ?? '—'}</span>
                    <span>ФН</span>
                    <span className="font-mono">{r.fnNumber ?? '—'}</span>
                    <span>ККТ</span>
                    <span className="font-mono">{r.kktRegNumber ?? '—'}</span>
                    <span>Сумма</span>
                    <span>{r.amount} {r.currency}</span>
                    <span>Попыток</span>
                    <span>{r.attempts}</span>
                    <span>Создан</span>
                    <span>{formatDateTime(r.createdAt)}</span>
                    {r.lastError && (
                      <>
                        <span>Last error</span>
                        <span className="text-red-600">{r.lastError}</span>
                      </>
                    )}
                  </div>
                  <CollapsibleJson data={r.rawRequest} label="rawRequest" />
                  <CollapsibleJson data={r.rawResponse} label="rawResponse" />
                </div>
              ))
            )}
          </TabsContent>

          <TabsContent value="events" className="space-y-2">
            {events.length === 0 ? (
              <div className="text-sm text-muted-foreground py-4">Событий нет</div>
            ) : (
              events.map((e) => (
                <div key={e.id} className="rounded border p-2 text-xs">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <LevelBadge level={e.level} />
                      <Badge variant="outline">{e.source}</Badge>
                      <Badge variant="outline">{e.type}</Badge>
                      {e.durationMs !== null && (
                        <span className="text-muted-foreground">{e.durationMs}ms</span>
                      )}
                    </div>
                    <span className="text-muted-foreground">{formatDateTime(e.createdAt)}</span>
                  </div>
                  <div className="mt-1">{e.message}</div>
                  <CollapsibleJson data={e.payload} label="payload" />
                </div>
              ))
            )}
          </TabsContent>

          <TabsContent value="webhooks" className="space-y-2">
            {webhooks.length === 0 ? (
              <div className="text-sm text-muted-foreground py-4">Webhook'ов нет</div>
            ) : (
              webhooks.map((w) => (
                <div key={w.id} className="rounded border p-2 text-xs">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{w.provider}</Badge>
                      <Badge variant="outline">{w.method}</Badge>
                      <span className="font-mono text-muted-foreground">{w.endpoint}</span>
                      {w.hmacVerified === true && (
                        <Badge variant="outline" className="bg-green-50 text-green-700">
                          <CheckCircle2 className="size-3 mr-1" /> HMAC OK
                        </Badge>
                      )}
                      {w.hmacVerified === false && (
                        <Badge variant="outline" className="bg-red-50 text-red-700">
                          <AlertCircle className="size-3 mr-1" /> HMAC FAIL
                        </Badge>
                      )}
                      {w.responseStatus !== null && (
                        <Badge variant="outline">HTTP {w.responseStatus}</Badge>
                      )}
                    </div>
                    <span className="text-muted-foreground">{formatDateTime(w.receivedAt)}</span>
                  </div>
                  <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-muted-foreground">
                    {w.ip && <><span>IP</span><span className="font-mono">{w.ip}</span></>}
                    {w.durationMs !== null && <><span>Duration</span><span>{w.durationMs}ms</span></>}
                    {w.signatureHeader && (
                      <><span>sign</span><span className="font-mono break-all">{w.signatureHeader.slice(0, 40)}…</span></>
                    )}
                    {w.errorText && <><span>Error</span><span className="text-red-600">{w.errorText}</span></>}
                  </div>
                  <CollapsibleJson data={w.rawBody} label="rawBody" />
                  <CollapsibleJson data={w.responseBody} label="responseBody" />
                </div>
              ))
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
